class DynamicSwiper {
  constructor(selector = '.swiper-section') {
    // This is the wrapper selector for each swiper section
    this.selector = selector;

    // We store each swiper instance here using the section id
    this.instances = new Map();

    // Used to prevent resize from firing too many times
    this.resizeTimer = null;

    this.init();
  }

  init() {
    this.initOnPageLoad();
    this.handleResize();
    this.handleShopifyEditorEvents();
  }

  initOnPageLoad() {
    const startSwipers = () => {
      // Wait a little so the layout is fully painted first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initSwipers(document);
          this.updateAllSwipers();
        });
      });
    };

    if (document.readyState === 'complete') {
      startSwipers();
    } else {
      window.addEventListener('load', startSwipers, { once: true });
    }
  }

  initSwipers(scope = document) {
    const sections = scope.querySelectorAll(this.selector);

    sections.forEach((section) => {
      this.initSingleSwiper(section);
    });
  }

  initSingleSwiper(section) {
    if (!section) return;

    const sectionId = section.id;
    const swiperElement = section.querySelector('.swiper');

    // Stop if required parts are missing
    if (!sectionId || !swiperElement || typeof Swiper === 'undefined') return;

    // If this section already has a swiper, destroy it first
    this.destroySwiper(sectionId);

    const settings = this.getSectionSettings(section);
    const swiperOptions = this.buildSwiperOptions(section, settings);

    const swiperInstance = new Swiper(swiperElement, swiperOptions);

    this.instances.set(sectionId, swiperInstance);

    // Extra updates to help fix first-load layout issues
    this.safeUpdate(swiperInstance, 150);
    this.safeUpdate(swiperInstance, 400);
  }

  getSectionSettings(section) {
    const breakpoint = parseInt(section.dataset.breakpoint, 10) || 760;
    const isMobile = window.innerWidth <= breakpoint;

    let effect = isMobile
      ? section.dataset.effectMobile || 'slide'
      : section.dataset.effectDesktop || 'slide';

    const modules = [];

    // Only use material effect if the module exists
    if (effect === 'material') {
      if (typeof EffectMaterial !== 'undefined') {
        modules.push(EffectMaterial);
      } else {
        effect = 'slide';
      }
    }

    return {
      isMobile,
      modules,
      effect,
      slidesPerView: isMobile
        ? parseFloat(section.dataset.slidesCountMobile) || 1
        : parseFloat(section.dataset.slidesCountDesktop) || 2,
      spaceBetween: isMobile
        ? parseInt(section.dataset.spaceBetweenMobile, 10) || 10
        : parseInt(section.dataset.spaceBetweenDesktop, 10) || 16,
      centeredSlides: isMobile
        ? section.dataset.centerSlidesMobile === 'true'
        : section.dataset.centerSlidesDesktop === 'true',
      autoplayEnabled: section.dataset.autoplay === 'true',
      autoplayDelay: parseInt(section.dataset.autoplayDelay, 10) || 3000
    };
  }

  buildSwiperOptions(section, settings) {
    return {
      modules: settings.modules,
      slidesPerView: settings.slidesPerView,
      spaceBetween: settings.spaceBetween,
      centeredSlides: settings.centeredSlides,
      effect: settings.effect,
      grabCursor: true,
      loop: true,
      autoHeight: false,
      slideToClickedSlide: false,

      // Keep these simple for better stability
      observer: false,
      observeParents: false,
      watchSlidesProgress: false,

      autoplay: settings.autoplayEnabled
        ? {
            delay: settings.autoplayDelay,
            disableOnInteraction: false
          }
        : false,

      pagination: {
        el: section.querySelector('.swiper-pagination'),
        clickable: true
      },

      navigation: {
        prevEl: section.querySelector('.swiper-button-prev'),
        nextEl: section.querySelector('.swiper-button-next')
      },

      on: {
        init() {
          setTimeout(() => {
            this.update();
          }, 100);
        },

        imagesReady() {
          this.update();
        }
      }
    };
  }

  destroySwiper(sectionId) {
    const existingSwiper = this.instances.get(sectionId);

    if (existingSwiper && !existingSwiper.destroyed) {
      existingSwiper.destroy(true, true);
    }

    this.instances.delete(sectionId);
  }

  reInitSection(sectionElement) {
    const swiperSection = sectionElement.matches(this.selector)
      ? sectionElement
      : sectionElement.querySelector(this.selector);

    if (swiperSection) {
      this.initSingleSwiper(swiperSection);
    }
  }

  updateAllSwipers() {
    this.instances.forEach((swiper) => {
      if (swiper && !swiper.destroyed) {
        swiper.update();
      }
    });
  }

  safeUpdate(swiper, delay = 0) {
    setTimeout(() => {
      if (swiper && !swiper.destroyed) {
        swiper.update();
      }
    }, delay);
  }

  handleResize() {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);

      // Wait a bit before updating so resize does not spam updates
      this.resizeTimer = setTimeout(() => {
        this.updateAllSwipers();
      }, 200);
    });
  }

  handleShopifyEditorEvents() {
    document.addEventListener('shopify:section:load', (event) => {
      this.reInitSection(event.target);
      this.delayUpdateAll();
    });

    document.addEventListener('shopify:section:reorder', () => {
      this.initSwipers(document);
      this.delayUpdateAll();
    });

    document.addEventListener('shopify:section:select', (event) => {
      this.reInitSection(event.target);
      this.delayUpdateAll();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const parentSection = event.target.closest('.shopify-section');

      if (parentSection) {
        this.reInitSection(parentSection);
        this.delayUpdateAll();
      }
    });
  }

  delayUpdateAll() {
    setTimeout(() => {
      this.updateAllSwipers();
    }, 200);
  }
}

window.dynamicSwiperInstance = new DynamicSwiper();